// src/pages/Enseignants/PassagesGrades/PassagesGradesPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  FaGraduationCap,
  FaRedoAlt,
  FaSearch,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";

import { getDojos } from "../../../services/dojoServices";
import { getGroupsList } from "../../../services/groupServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllAdherentsListWithGroup,
  updateTeacherNotes,
} from "../../../services/adherentServices";

import GradeFiltersBar from "./components/GradeFiltersBar";
import GradeGroupHeader from "./components/GradeGroupHeader";
import GradeAdherentsGrid from "./components/GradeAdherentsGrid";
import GradeStatsCards from "./components/GradeStatsCards";
import GradeNoteDrawer from "./components/GradeNoteDrawer";

import {
  getGroupLabel,
  getTeacherFullName,
  parseGradePassage,
  seasonNeighbors,
  sortAdherentsForGrade,
  todayISO,
  upsertGradePassageLine,
} from "./utils/gradePassageUtils";

const PassagesGradesPage = () => {
  useScrollToTop();

  const { user } = useUser();

  const storedUser = useMemo(() => {
    try {
      const raw =
        localStorage.getItem("userInfo") ||
        localStorage.getItem("user") ||
        localStorage.getItem("currentUser");

      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const teacherName = useMemo(
    () => getTeacherFullName(user, storedUser),
    [user, storedUser]
  );

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");

  const [allGroups, setAllGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("");

  const [adherents, setAdherents] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");

  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState("all");
  const [sortBy, setSortBy] = useState("not_graded_first");
  const [hideStopped, setHideStopped] = useState(true);

  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const reloadAdherents = useCallback(async () => {
    try {
      const list = await getAllAdherentsListWithGroup();
      setAdherents(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
      toast.error("Impossible de recharger les adhérents.");
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const [settings, dojoList, groupList, adherentList] =
          await Promise.all([
            getSettings(),
            getDojos(),
            getGroupsList(),
            getAllAdherentsListWithGroup(),
          ]);

        if (Array.isArray(settings) && settings[0]?.saison_active) {
          const activeSeason = settings[0].saison_active;
          const { prev, next } = seasonNeighbors(activeSeason);

          setSeasons([prev, activeSeason, next]);
          setSelectedSeason(activeSeason);
        }

        const safeDojos = Array.isArray(dojoList) ? dojoList : [];
        const safeGroups = Array.isArray(groupList) ? groupList : [];

        setDojos(safeDojos);
        setAllGroups(safeGroups);

        if (safeDojos.length === 1) {
          const dojoId = String(safeDojos[0].id);
          setSelectedDojo(dojoId);
          setGroups(
            safeGroups.filter((group) => String(group.dojo_id) === dojoId)
          );
        } else {
          setGroups(safeGroups);
        }

        setAdherents(Array.isArray(adherentList) ? adherentList : []);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors du chargement de la page.");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleDojoChange = useCallback(
    (dojoId) => {
      setSelectedDojo(dojoId);

      const nextGroups = dojoId
        ? allGroups.filter((group) => String(group.dojo_id) === String(dojoId))
        : allGroups;

      setGroups(nextGroups || []);
      setFilterGroup("");
    },
    [allGroups]
  );

  const allowedGroupNames = useMemo(
    () => new Set((groups || []).map(getGroupLabel).filter(Boolean)),
    [groups]
  );

  const visibleAdherents = useMemo(() => {
    if (!filterGroup) return [];

    const q = search.trim().toLowerCase();

    const filtered = (adherents || []).filter((adherent) => {
      const adherentGroups = adherent?.Groups || [];
      const gradeInfo = parseGradePassage(adherent?.notes_enseignant);

      const inDojo =
        !selectedDojo ||
        adherentGroups.some((group) => allowedGroupNames.has(getGroupLabel(group)));

      const inGroup =
        !filterGroup ||
        adherentGroups.some((group) => getGroupLabel(group) === filterGroup);

      const inSeason =
        !selectedSeason || adherent?.saison_active === selectedSeason;

      const notStopped =
        !hideStopped || adherent?.statut_inscription !== "Licencié - Arrêt";

      const matchSearch =
        !q ||
        String(adherent?.nom || "").toLowerCase().includes(q) ||
        String(adherent?.prenom || "").toLowerCase().includes(q);

      const matchResult = (() => {
        if (resultFilter === "all") return true;
        if (resultFilter === "graded") return gradeInfo.isGraded;
        if (resultFilter === "not_graded") return !gradeInfo.isGraded;

        const result = String(gradeInfo.result || "").toLowerCase();

        if (resultFilter === "obtained") {
          return gradeInfo.isGraded && result.includes("obtenu") && !result.includes("non");
        }

        if (resultFilter === "failed") {
          return gradeInfo.isGraded && result.includes("non");
        }

        return true;
      })();

      return (
        inDojo &&
        inGroup &&
        inSeason &&
        notStopped &&
        matchSearch &&
        matchResult
      );
    });

    return sortAdherentsForGrade(filtered, sortBy);
  }, [
    adherents,
    allowedGroupNames,
    filterGroup,
    hideStopped,
    resultFilter,
    search,
    selectedDojo,
    selectedSeason,
    sortBy,
  ]);

  const stats = useMemo(() => {
    const initial = {
      total: visibleAdherents.length,
      graded: 0,
      notGraded: 0,
      obtained: 0,
      failed: 0,
    };

    return visibleAdherents.reduce((acc, adherent) => {
      const info = parseGradePassage(adherent?.notes_enseignant);
      const result = String(info.result || "").toLowerCase();

      if (!info.isGraded) {
        acc.notGraded += 1;
        return acc;
      }

      acc.graded += 1;

      if (result.includes("non")) {
        acc.failed += 1;
      } else if (result.includes("obtenu")) {
        acc.obtained += 1;
      }

      return acc;
    }, initial);
  }, [visibleAdherents]);

  const openDrawer = useCallback((adherent) => {
    setSelectedAdherent(adherent);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedAdherent(null);
  }, []);

  const handleQuickGrade = useCallback(
    async (adherent, result = "Obtenu") => {
      if (!adherent?.id) return;

      try {
        setSavingId(adherent.id);

        const nextNotes = upsertGradePassageLine(adherent.notes_enseignant, {
          date: todayISO(),
          result,
          teacher: teacherName,
          comment: "",
        });

        await updateTeacherNotes(adherent.id, {
          notes_enseignant: nextNotes,
        });

        toast.success(
          `${adherent.prenom || ""} ${adherent.nom || ""} marqué(e) : ${result}.`
        );

        await reloadAdherents();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la mise à jour du passage de grade.");
      } finally {
        setSavingId(null);
      }
    },
    [reloadAdherents, teacherName]
  );

  const handleSaveDrawerNotes = useCallback(
    async (notesText) => {
      if (!selectedAdherent?.id) return;

      try {
        setSavingId(selectedAdherent.id);

        await updateTeacherNotes(selectedAdherent.id, {
          notes_enseignant: notesText,
        });

        toast.success("Suivi du passage de grade mis à jour.");
        await reloadAdherents();
        closeDrawer();
      } catch (error) {
        console.error(error);
        toast.error("Impossible d'enregistrer les notes enseignant.");
      } finally {
        setSavingId(null);
      }
    },
    [closeDrawer, reloadAdherents, selectedAdherent]
  );

  return (
    <section className="min-h-[calc(100vh-80px)] bg-slate-50 px-3 py-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-5 lg:px-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 pt-[130px]">
        <header className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative overflow-hidden bg-slate-950 px-5 py-6 text-white sm:px-7">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.35),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.28),transparent_36%)]" />

            <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] text-white/80">
                  <FaGraduationCap />
                  Enseignants
                </div>

                <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Suivi des passages de grade
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-200">
                  Sélectionnez un groupe, visualisez les adhérents, puis indiquez
                  rapidement qui a été noté, à quelle date, avec le résultat et
                  le nom de l’enseignant.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                  <p className="text-xs text-slate-300">Enseignant</p>
                  <p className="mt-1 text-sm font-bold">{teacherName}</p>
                </div>

                <button
                  type="button"
                  onClick={reloadAdherents}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm font-bold text-slate-950 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaRedoAlt className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 px-5 py-5 dark:border-slate-800 sm:px-7">
            <GradeFiltersBar
              loading={loading}
              dojos={dojos}
              selectedDojo={selectedDojo}
              onDojoChange={handleDojoChange}
              groups={groups}
              filterGroup={filterGroup}
              onGroupChange={setFilterGroup}
              seasons={seasons}
              selectedSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
              search={search}
              onSearchChange={setSearch}
              resultFilter={resultFilter}
              onResultFilterChange={setResultFilter}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              hideStopped={hideStopped}
              onToggleHideStopped={setHideStopped}
            />
          </div>

          {filterGroup ? (
            <>
              <div className="px-5 pt-5 sm:px-7">
                <GradeGroupHeader
                  groupName={filterGroup}
                  count={visibleAdherents.length}
                  stats={stats}
                />
              </div>

              <div className="px-5 py-5 sm:px-7">
                <GradeStatsCards stats={stats} />
              </div>

              <div className="px-5 pb-7 sm:px-7">
                {loading ? (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-72 animate-pulse rounded-[26px] bg-slate-100 dark:bg-slate-800"
                      />
                    ))}
                  </div>
                ) : visibleAdherents.length ? (
                  <GradeAdherentsGrid
                    adherents={visibleAdherents}
                    savingId={savingId}
                    onQuickGrade={handleQuickGrade}
                    onOpenDrawer={openDrawer}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-[26px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center dark:border-slate-700 dark:bg-slate-900/50">
                    <FaSearch className="text-3xl text-slate-400" />
                    <p className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-100">
                      Aucun adhérent trouvé
                    </p>
                    <p className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
                      Modifiez la recherche, le filtre de résultat ou la saison.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center px-5 py-14 text-center sm:px-7">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                <FaUsers className="text-2xl" />
              </div>
              <p className="mt-4 text-base font-bold text-slate-900 dark:text-white">
                Sélectionnez un groupe
              </p>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                La page affiche ensuite les cards des adhérents du groupe avec
                leur statut de passage de grade.
              </p>
            </div>
          )}
        </header>
      </div>

      <GradeNoteDrawer
        isOpen={drawerOpen}
        adherent={selectedAdherent}
        teacherName={teacherName}
        saving={savingId === selectedAdherent?.id}
        onClose={closeDrawer}
        onSave={handleSaveDrawerNotes}
      />

      <ToastContainer position="bottom-right" />
    </section>
  );
};

export default PassagesGradesPage;